import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { useMountEffect } from "@/lib/useMountEffect";
import { Save, RotateCcw, Mail, Eye, Send } from "lucide-react";

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [editor, setEditor] = useState({ subject: "", body_html: "", enabled: true });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setTemplates(data);
      if (data.length && !active) {
        setActive(data[0].key);
        setEditor({ subject: data[0].subject, body_html: data[0].body_html, enabled: data[0].enabled !== false });
      }
    } catch (e) { setError(formatApiError(e)); }
  };

  useMountEffect(load);

  useEffect(() => {
    api.get("/email-logs").then((r) => setLogs(r.data)).catch(() => {});
  }, []);

  const current = templates.find((t) => t.key === active);

  const selectTemplate = (key) => {
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    setActive(key);
    setEditor({ subject: t.subject, body_html: t.body_html, enabled: t.enabled !== false });
    setPreview(null);
    setSavedAt(null);
    setTestResult(null);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(`/email-templates/${active}`, {
        subject: editor.subject,
        body_html: editor.body_html,
        enabled: editor.enabled,
      });
      setTemplates((ts) => ts.map((t) => (t.key === active ? data : t)));
      setSavedAt(new Date());
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset this template to defaults?")) return;
    try {
      const { data } = await api.post(`/email-templates/${active}/reset`);
      setTemplates((ts) => ts.map((t) => (t.key === active ? data : t)));
      setEditor({ subject: data.subject, body_html: data.body_html });
      setSavedAt(new Date());
    } catch (e) { setError(formatApiError(e)); }
  };

  const buildPreview = async () => {
    if (!current) return;
    const sampleVars = {};
    (current.variables || []).forEach((v) => {
      sampleVars[v] = v === "name" ? "Alex Sample" : v === "discipline" ? "Project Manager" : v === "rate" ? "$1200 (daily)" : v === "availability" ? "Available within 2 weeks" : v === "organisation" ? "Acme Corp" : `[${v}]`;
    });
    try {
      // Save current draft first (preview reflects DB)
      await api.put(`/email-templates/${active}`, { subject: editor.subject, body_html: editor.body_html });
      const { data } = await api.post(`/email-templates/${active}/preview`, { variables: sampleVars });
      setPreview(data);
    } catch (e) { setError(formatApiError(e)); }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    setError("");
    setTestResult(null);
    try {
      // Persist current draft before testing so the test reflects unsaved edits
      await api.put(`/email-templates/${active}`, {
        subject: editor.subject, body_html: editor.body_html, enabled: editor.enabled,
      });
      const { data } = await api.post(`/email-templates/${active}/send-test`, {
        to: testTo.trim() || undefined,
      });
      setTestResult(data);
      // Refresh logs so the new send appears
      api.get("/email-logs").then((r) => setLogs(r.data)).catch(() => {});
    } catch (e) { setError(formatApiError(e)); }
    finally { setTesting(false); }
  };

  return (
    <AdminLayout
      title="Email templates"
      subtitle="Edit automated emails sent to candidates and clients. Use {{variable}} placeholders."
      actions={
        <>
          <button onClick={reset} className="btn-secondary" data-testid="template-reset">
            <RotateCcw className="h-4 w-4" /> Reset to default
          </button>
          <button onClick={buildPreview} className="btn-secondary" data-testid="template-preview">
            <Eye className="h-4 w-4" /> Preview
          </button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="template-save">
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save changes"}
          </button>
        </>
      }
    >
      {error && <div className="mb-4 text-sm text-destructive" data-testid="template-error">{error}</div>}
      {savedAt && <div className="mb-4 text-xs font-mono text-execution">Saved {savedAt.toLocaleTimeString()}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Templates list */}
        <aside className="lg:col-span-3" data-testid="templates-list">
          <div className="border border-border bg-white divide-y divide-border">
            {templates.map((t) => (
              <button
                key={t.key}
                onClick={() => selectTemplate(t.key)}
                className={`w-full text-left p-4 transition-colors ${active === t.key ? "bg-obsidian text-white" : "hover:bg-muted"}`}
                data-testid={`template-item-${t.key}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Mail className={`h-3.5 w-3.5 ${active === t.key ? "text-execution" : "text-muted-foreground"}`} />
                  <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{t.key}</span>
                </div>
                <div className="font-heading font-semibold text-sm leading-tight">{t.name}</div>
              </button>
            ))}
          </div>
        </aside>

        {/* Editor */}
        <div className="lg:col-span-9 space-y-4">
          {current ? (
            <>
              <div className="border border-border bg-white p-5">
                <div className="overline mb-2">Template</div>
                <div className="font-heading text-2xl font-bold mb-1">{current.name}</div>
                <div className="text-sm text-muted-foreground">{current.description}</div>
                {current.variables?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground self-center mr-2">Variables:</span>
                    {current.variables.map((v) => (
                      <code key={v} className="chip-accent">{`{{${v}}}`}</code>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-border bg-white p-5 space-y-4" data-testid="template-editor">
                <div className="flex items-center justify-between gap-4 pb-4 border-b border-border">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="template-enabled-label">
                    <input
                      type="checkbox"
                      checked={editor.enabled}
                      onChange={(e) => setEditor((d) => ({ ...d, enabled: e.target.checked }))}
                      className="h-4 w-4 accent-execution"
                      data-testid="template-enabled"
                    />
                    <span className="text-sm">
                      <span className="font-semibold">Enabled</span>
                      <span className="text-muted-foreground ml-2">— when off, this auto-response is silently skipped (submissions still save).</span>
                    </span>
                  </label>
                  <span
                    className={`chip ${editor.enabled ? "bg-[#00C779] text-white border-[#00C779]" : "bg-muted text-muted-foreground border-border"}`}
                    data-testid="template-enabled-badge"
                  >
                    {editor.enabled ? "Active" : "Disabled"}
                  </span>
                </div>
                <label className="block">
                  <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Subject</span>
                  <input
                    className="input-swiss"
                    value={editor.subject}
                    onChange={(e) => setEditor((d) => ({ ...d, subject: e.target.value }))}
                    data-testid="template-subject"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Body (HTML)</span>
                  <textarea
                    className="input-swiss font-mono text-xs min-h-[400px] resize-y"
                    value={editor.body_html}
                    onChange={(e) => setEditor((d) => ({ ...d, body_html: e.target.value }))}
                    data-testid="template-body"
                  />
                </label>
              </div>

              {/* Send Test Email panel */}
              <div className="border border-border bg-zinc-50 p-5" data-testid="template-send-test-panel">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="overline mb-1">Send Test Email</div>
                    <p className="text-sm text-muted-foreground">
                      Renders this template with sample variables and sends through the live provider (Resend). Result is recorded in <code>email_logs</code>.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={sendTestEmail}
                    disabled={testing}
                    className="btn-primary text-xs whitespace-nowrap disabled:opacity-60"
                    data-testid="template-send-test"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {testing ? "Sending..." : "Send test"}
                  </button>
                </div>
                <input
                  type="email"
                  className="input-swiss"
                  placeholder="Recipient (defaults to your admin email)"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  data-testid="template-test-to"
                />
                {testResult && (
                  <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono" data-testid="template-test-result">
                    <div className="text-muted-foreground">Status</div>
                    <div className={testResult.status === "sent" ? "text-emerald-700" : "text-red-700"}>
                      {testResult.status}
                    </div>
                    <div className="text-muted-foreground">Recipient</div>
                    <div>{testResult.to}</div>
                    {testResult.message_id && (<>
                      <div className="text-muted-foreground">Message ID</div>
                      <div className="break-all">{testResult.message_id}</div>
                    </>)}
                    {testResult.error && (<>
                      <div className="text-muted-foreground">Error</div>
                      <div className="text-red-700 break-all">{testResult.error}</div>
                    </>)}
                  </div>
                )}
              </div>

              {preview && (
                <div className="border border-border bg-white" data-testid="template-preview-panel">
                  <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div>
                      <div className="overline">Preview · with sample variables</div>
                      <div className="font-heading text-lg font-semibold mt-1">{preview.subject}</div>
                    </div>
                    <Send className="h-4 w-4 text-execution" />
                  </div>
                  <iframe
                    title="Email preview"
                    srcDoc={preview.html}
                    className="w-full h-[640px] bg-obsidian"
                  />
                </div>
              )}

              {/* Email logs */}
              <div className="border border-border bg-white" data-testid="email-logs">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                  <div className="overline">Recent sends</div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{logs.length} entries</span>
                </div>
                {logs.length === 0 ? (
                  <div className="p-6 text-sm text-center text-muted-foreground">No emails sent yet.</div>
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {logs.slice(0, 15).map((l) => (
                      <li key={l.id} className="px-5 py-3 grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-3 truncate">
                          <span className={`chip ${
                            l.status === "sent" ? "bg-[#00C779] text-white border-[#00C779]" :
                            l.status === "mocked" ? "bg-execution text-white border-execution" :
                            "bg-destructive text-white border-destructive"
                          }`}>{l.status}</span>
                        </div>
                        <div className="col-span-3 truncate text-xs">{l.to}</div>
                        <div className="col-span-4 truncate text-xs text-muted-foreground">{l.subject}</div>
                        <div className="col-span-2 text-right font-mono text-[10px] text-muted-foreground">
                          {new Date(l.sent_at).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Loading templates…</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
